import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usersService, AdminUserDetail } from '../../services/users.service'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { formatCurrency, formatDateTime } from '../../utils/formatters'

function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [vaEditing, setVaEditing] = useState(false)
  const [vaNumber, setVaNumber] = useState('')
  const [vaBank, setVaBank] = useState('')
  const [vaSaving, setVaSaving] = useState(false)
  const [vaError, setVaError] = useState<string | null>(null)
  // Card issue (manual record of the physical membership card)
  const [ciEditing, setCiEditing] = useState(false)
  const [ciIssuedAt, setCiIssuedAt] = useState('')
  const [ciSaving, setCiSaving] = useState(false)
  const [ciError, setCiError] = useState<string | null>(null)

  // VA Balance (informational amount, audited; shared endpoint with the Finance page)
  const [vabEditing, setVabEditing] = useState(false)
  const [vabValue, setVabValue] = useState('')
  const [vabNote, setVabNote] = useState('')
  const [vabSaving, setVabSaving] = useState(false)
  const [vabError, setVabError] = useState<string | null>(null)

  // Reset password (admin sets a new password for the member)
  const [pwEditing, setPwEditing] = useState(false)
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwShow, setPwShow] = useState(false)
  const [pwReset2fa, setPwReset2fa] = useState(true)
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError] = useState<string | null>(null)

  const fetchUser = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await usersService.getUserDetail(id)
      setUser(data)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load user')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  const handleActivate = async () => {
    if (!user) return
    try {
      setActionLoading(true)
      await usersService.activateUser(user.id)
      await fetchUser()
    } catch {
      // toast handled by interceptor
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeactivate = async () => {
    if (!user) return
    const fullName = `${user.firstName} ${user.lastName}`.trim()
    const confirmed = window.confirm(
      t('userManagement.confirmDeactivate', {
        name: fullName || user.email,
        defaultValue: `Deactivate ${fullName || user.email}? They will not be able to log in until reactivated.`,
      }),
    )
    if (!confirmed) return
    try {
      setActionLoading(true)
      await usersService.deactivateUser(user.id)
      await fetchUser()
    } catch {
      // toast handled by interceptor
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveVa = async () => {
    if (!user) return
    const number = vaNumber.replace(/[\s-]/g, '')
    if (!/^\d{6,20}$/.test(number)) {
      setVaError(t('userManagement.virtualAccountInvalidNumber', 'VA number must be 6-20 digits'))
      return
    }
    if (!vaBank.trim()) {
      setVaError(t('userManagement.virtualAccountBankRequired', 'Bank name is required'))
      return
    }
    try {
      setVaSaving(true)
      setVaError(null)
      await usersService.setVirtualAccount(user.id, number, vaBank.trim())
      setVaEditing(false)
      await fetchUser()
    } catch (err: any) {
      setVaError(err?.response?.data?.message || 'Failed to save virtual account')
    } finally {
      setVaSaving(false)
    }
  }

  const handleRemoveVa = async () => {
    if (!user) return
    if (!window.confirm(t('userManagement.virtualAccountConfirmClear', 'Remove this virtual account? The member will no longer see it.'))) return
    try {
      setVaSaving(true)
      setVaError(null)
      await usersService.clearVirtualAccount(user.id)
      setVaEditing(false)
      await fetchUser()
    } catch (err: any) {
      setVaError(err?.response?.data?.message || 'Failed to remove virtual account')
    } finally {
      setVaSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || t('userManagement.notFound', 'User not found')}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('userManagement.backToList', 'Back to Users')}
          </button>
        </div>
      </div>
    )
  }

  const fullName = `${user.firstName} ${user.lastName}`.trim()

  // TEMPORARY: VA assignment is paused for Singapore-based users (mirrors the
  // server-side block; the '.includes' matches both 'Singapore' and legacy
  // 'Singaporean' values).
  const isSgUser = (user.nationality ?? '').toLowerCase().includes('singapor')

  const handleSavePassword = async () => {
    if (!user) return
    if (!pwNew || pwNew.length < 8) {
      setPwError('Password must be at least 8 characters')
      return
    }
    if (pwNew !== pwConfirm) {
      setPwError('Passwords do not match')
      return
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(pwNew)) {
      setPwError('Password must contain an uppercase letter, a lowercase letter, a number, and a special character')
      return
    }
    if (!window.confirm(`Reset the password for ${user.firstName} ${user.lastName}? Give the new password to the member and ask them to change it after logging in.`)) {
      return
    }
    try {
      setPwSaving(true)
      await usersService.resetPassword(user.id, pwNew, pwConfirm, pwReset2fa)
      setPwEditing(false)
      setPwNew('')
      setPwConfirm('')
      setPwError(null)
      await fetchUser()
      toast.success('Password reset successfully')
    } catch (err: any) {
      setPwError(err?.response?.data?.message || 'Failed to reset password')
    } finally {
      setPwSaving(false)
    }
  }

  const toDateTimeLocal = (iso?: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const handleSaveCardIssue = async () => {
    if (!user) return
    if (!ciIssuedAt) {
      setCiError('Please provide the issue date & time')
      return
    }
    try {
      setCiSaving(true)
      await usersService.setCardIssue(user.id, ciIssuedAt)
      setCiEditing(false)
      setCiError(null)
      await fetchUser()
    } catch (err: any) {
      setCiError(err?.response?.data?.message || 'Failed to save card issue')
    } finally {
      setCiSaving(false)
    }
  }

  const handleSaveVab = async () => {
    if (!user) return
    const trimmed = vabValue.trim()
    if (!trimmed || Number.isNaN(Number(trimmed))) {
      setVabError(t('finance.invalid', 'Enter a valid amount (max 2 decimal places)'))
      return
    }
    const balance = Number(trimmed)
    if (balance < 0) {
      setVabError(t('finance.negative', 'VA Balance cannot be negative'))
      return
    }
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      setVabError(t('finance.invalid', 'Enter a valid amount (max 2 decimal places)'))
      return
    }
    try {
      setVabSaving(true)
      setVabError(null)
      const result = await usersService.setVaBalance(user.id, balance, vabNote.trim() || undefined)
      setVabEditing(false)
      setVabNote('')
      if (result.unchanged) {
        toast.success(t('finance.unchanged', 'No change to save'))
      } else {
        toast.success(t('finance.saved', 'VA Balance updated'))
      }
      await fetchUser()
    } catch (err: any) {
      setVabError(err?.response?.data?.message || 'Failed to save VA Balance')
    } finally {
      setVabSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-5xl mx-auto px-6">
        {/* Back link */}
        <button
          onClick={() => navigate('/admin/users')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
        >
          ← {t('userManagement.backToList', 'Back to Users')}
        </button>

        {/* Header card */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{fullName}</h1>
              <p className="text-gray-600">{user.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                  {user.memberId}
                </span>
                <span className="px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {t(`userManagement.roles.${user.role}`, user.role)}
                </span>
              </div>
            </div>
            <div>
              {user.isActive ? (
                <button
                  onClick={handleDeactivate}
                  disabled={actionLoading || user.role === 'ADMIN'}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={
                    user.role === 'ADMIN'
                      ? t('userManagement.cannotDeactivateAdmin', 'Admin accounts cannot be deactivated')
                      : undefined
                  }
                >
                  {t('userManagement.deactivate', 'Deactivate')}
                </button>
              ) : (
                <button
                  onClick={handleActivate}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-medium text-green-600 border border-green-300 rounded-lg hover:bg-green-50 disabled:opacity-40"
                >
                  {t('userManagement.activate', 'Activate')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status badges */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('userManagement.accountStatus', 'Account Status')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatusBadge
              label={t('userManagement.active', 'Active')}
              ok={user.isActive}
              okLabel={t('userManagement.active', 'Active')}
              notLabel={t('userManagement.inactive', 'Inactive')}
            />
            <StatusBadge
              label={t('userManagement.verified', 'Verified')}
              ok={user.isVerified}
              okLabel={t('userManagement.verified', 'Verified')}
              notLabel={t('userManagement.unverified', 'Unverified')}
            />
            <StatusBadge
              label={t('userManagement.approved', 'Approved')}
              ok={user.isApproved}
              okLabel={t('userManagement.approved', 'Approved')}
              notLabel={t('userManagement.notApproved', 'Not approved')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal info */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('userManagement.personalInfo', 'Personal Information')}
            </h2>
            <dl className="space-y-3 text-sm">
              <DetailRow label={t('userManagement.fullName', 'Full Name')} value={fullName} />
              <DetailRow label={t('userManagement.email', 'Email')} value={user.email} />
              <DetailRow label={t('userManagement.phone', 'Phone')} value={user.phone || '—'} />
              <DetailRow label={t('userManagement.nationality', 'Nationality')} value={user.nationality || '—'} />
              <DetailRow label={t('userManagement.passport', 'Passport / IC')} value={user.passportNumber || '—'} />
              <DetailRow label={t('userManagement.gender', 'Gender')} value={user.gender || '—'} />
              <DetailRow
                label={t('userManagement.address', 'Address')}
                value={
                  [user.address, user.city, user.state, user.postalCode]
                    .filter(Boolean)
                    .join(', ') || '—'
                }
              />
            </dl>
          </div>

          {/* Wallet + provider */}
          <div className="space-y-6">
            {user.wallet ? (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('userManagement.wallet', 'Wallet')}
                </h2>
                <dl className="space-y-3 text-sm">
                  <DetailRow
                    label={t('userManagement.walletNumber', 'Wallet Number')}
                    value={<span className="font-mono">{user.wallet.walletNumber}</span>}
                  />
                  <DetailRow label={t('userManagement.balance', 'Balance')} value={`${user.wallet.balance} ${user.wallet.currency}`} />
                  <DetailRow
                    label={t('userManagement.activated', 'Wallet Activated')}
                    value={user.activated ? t('userManagement.yes', 'Yes') : t('userManagement.no', 'No')}
                  />
                  <DetailRow
                    label={t('userManagement.walletStatus', 'Wallet Status')}
                    value={
                      user.wallet.isFrozen
                        ? t('userManagement.frozen', 'Frozen')
                        : user.wallet.isActive
                        ? t('userManagement.active', 'Active')
                        : t('userManagement.inactive', 'Inactive')
                    }
                  />
                  {user.wallet.freezeReason && (
                    <DetailRow label={t('userManagement.freezeReason', 'Freeze Reason')} value={user.wallet.freezeReason} />
                  )}
                </dl>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('userManagement.wallet', 'Wallet')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('userManagement.noWallet', 'No wallet created yet (created on first access).')}
                </p>
              </div>
            )}

            {/* Virtual account */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('userManagement.virtualAccount', 'Virtual Account')}
                </h2>
                {!vaEditing && (
                  <div className="flex items-center gap-2">
                    {user.virtualAccount && !isSgUser && (
                      <button
                        onClick={() => {
                          setVaNumber(user.virtualAccount!.number)
                          setVaBank(user.virtualAccount!.bank || '')
                          setVaError(null)
                          setVaEditing(true)
                        }}
                        disabled={vaSaving}
                        className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-40"
                      >
                        {t('userManagement.virtualAccountEdit', 'Edit')}
                      </button>
                    )}
                    {user.virtualAccount && (
                      <button
                        onClick={handleRemoveVa}
                        disabled={vaSaving}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-40"
                      >
                        {t('userManagement.virtualAccountRemove', 'Remove')}
                      </button>
                    )}
                    {!user.virtualAccount && !isSgUser && (
                      <button
                        onClick={() => {
                          setVaNumber('')
                          setVaBank('')
                          setVaError(null)
                          setVaEditing(true)
                        }}
                        disabled={vaSaving}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-[#0D7330] rounded-lg hover:bg-[#0a5c27] disabled:opacity-40"
                      >
                        {t('userManagement.virtualAccountAssign', 'Assign Virtual Account')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {isSgUser && !vaEditing && (
                <p className="mt-3 text-sm text-amber-700">
                  {t(
                    'userManagement.virtualAccountSgUnavailable',
                    'Virtual accounts are temporarily unavailable for Singapore-based users.'
                  )}
                </p>
              )}

              {vaEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('userManagement.virtualAccountNumber', 'Account Number')}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={vaNumber}
                      onChange={(e) => setVaNumber(e.target.value)}
                      placeholder="e.g. 88771234567890"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('userManagement.virtualAccountBank', 'Bank')}
                    </label>
                    <input
                      type="text"
                      value={vaBank}
                      onChange={(e) => setVaBank(e.target.value)}
                      placeholder="e.g. Maybank"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                    />
                  </div>
                  {vaError && <p className="text-red-600 text-xs">{vaError}</p>}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveVa}
                      disabled={vaSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#0D7330] rounded-lg hover:bg-[#0a5c27] disabled:opacity-40"
                    >
                      {vaSaving
                        ? '...'
                        : t('userManagement.virtualAccountSave', 'Save')}
                    </button>
                    <button
                      onClick={() => {
                        setVaEditing(false)
                        setVaError(null)
                      }}
                      disabled={vaSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      {t('userManagement.virtualAccountCancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : user.virtualAccount ? (
                <dl className="space-y-3 text-sm">
                  <DetailRow
                    label={t('userManagement.virtualAccountNumber', 'Account Number')}
                    value={<span className="font-mono">{user.virtualAccount.number}</span>}
                  />
                  <DetailRow
                    label={t('userManagement.virtualAccountBank', 'Bank')}
                    value={user.virtualAccount.bank || '—'}
                  />
                  <DetailRow
                    label={t('userManagement.virtualAccountAssignedAt', 'Assigned At')}
                    value={user.virtualAccount.assignedAt ? new Date(user.virtualAccount.assignedAt).toLocaleString() : '—'}
                  />
                  <DetailRow
                    label={t('userManagement.virtualAccountAssignedBy', 'Assigned By')}
                    value={
                      user.virtualAccount.assignedBy
                        ? `${user.virtualAccount.assignedBy.firstName} ${user.virtualAccount.assignedBy.lastName}`.trim()
                        : '—'
                    }
                  />
                </dl>
              ) : (
                <p className="text-sm text-gray-500">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
                      {t('userManagement.comingSoon', 'Coming Soon')}
                    </span>
                    <span className="italic">
                      {t('userManagement.virtualAccountNotAssigned', 'Not assigned yet')}
                    </span>
                  </span>
                </p>
              )}
            </div>

            {/* VA Balance (informational, audited — not the wallet balance) */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('finance.vaBalance', 'VA Balance')}
                </h2>
                {!vabEditing && (
                  <button
                    onClick={() => {
                      setVabValue(
                        user.virtualAccountBalance === null || user.virtualAccountBalance === undefined
                          ? ''
                          : String(user.virtualAccountBalance),
                      )
                      setVabNote('')
                      setVabError(null)
                      setVabEditing(true)
                    }}
                    disabled={vabSaving}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-40"
                  >
                    {t('finance.edit', 'Edit')}
                  </button>
                )}
              </div>

              {user.virtualAccountBalance !== null &&
                user.virtualAccountBalance !== undefined &&
                !user.virtualAccount && (
                  <p className="mb-3 text-sm text-amber-700">
                    {t('finance.noVaNumber', 'No bank virtual account assigned to this member.')}
                  </p>
                )}

              {vabEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('finance.vaBalance', 'VA Balance')} (RM)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={vabValue}
                      onChange={(e) => setVabValue(e.target.value)}
                      placeholder="e.g. 1250.50"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('finance.note', 'Note (optional)')}
                    </label>
                    <input
                      type="text"
                      maxLength={200}
                      value={vabNote}
                      onChange={(e) => setVabNote(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {t(
                      'finance.informationalNote',
                      'Informational only — this balance is maintained manually and is not the wallet balance.'
                    )}
                  </p>
                  {vabError && <p className="text-red-600 text-xs">{vabError}</p>}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveVab}
                      disabled={vabSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#0D7330] rounded-lg hover:bg-[#0a5c27] disabled:opacity-40"
                    >
                      {vabSaving ? '...' : t('finance.save', 'Save')}
                    </button>
                    <button
                      onClick={() => {
                        setVabEditing(false)
                        setVabError(null)
                      }}
                      disabled={vabSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      {t('finance.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <dl className="space-y-3 text-sm">
                    <DetailRow
                      label={t('finance.vaBalance', 'VA Balance')}
                      value={
                        user.virtualAccountBalance === null ||
                        user.virtualAccountBalance === undefined
                          ? t('finance.notSet', 'Not set')
                          : formatCurrency(user.virtualAccountBalance)
                      }
                    />
                  </dl>
                  {user.vaBalanceLogs && user.vaBalanceLogs.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        {t('finance.history', 'VA Balance History')}
                      </p>
                      <ul className="space-y-2">
                        {user.vaBalanceLogs.slice(0, 5).map((log) => (
                          <li key={log.id} className="text-xs text-gray-600">
                            <span className="text-gray-400">{formatDateTime(log.createdAt)}</span>{' '}
                            —{' '}
                            {log.changedBy
                              ? `${log.changedBy.firstName} ${log.changedBy.lastName}`.trim()
                              : '—'}{' '}
                            · {formatCurrency(log.previousBalance ?? 0)} →{' '}
                            {formatCurrency(log.newBalance)}
                            {log.note && <span className="text-gray-400"> · {log.note}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Card issued (manual record of the physical membership card) */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('userManagement.cardIssued', 'Card Issued')}
                </h2>
                {!ciEditing && (
                  <button
                    onClick={() => {
                      setCiIssuedAt(toDateTimeLocal(user.cardIssuedAt))
                      setCiError(null)
                      setCiEditing(true)
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
                  >
                    {t('userManagement.cardIssuedUpdate', 'Update')}
                  </button>
                )}
              </div>

              {ciEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('userManagement.cardIssuedAt', 'Date & Time Issued')}
                    </label>
                    <input
                      type="datetime-local"
                      value={ciIssuedAt}
                      onChange={(e) => setCiIssuedAt(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {t(
                      'userManagement.cardIssuedByNote',
                      'The issuer is recorded automatically as the admin who saves this form.'
                    )}
                  </p>
                  {ciError && <p className="text-red-600 text-xs">{ciError}</p>}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveCardIssue}
                      disabled={ciSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#0D7330] rounded-lg hover:bg-[#0a5c27] disabled:opacity-40"
                    >
                      {ciSaving ? '...' : t('userManagement.cardIssuedSave', 'Save')}
                    </button>
                    <button
                      onClick={() => {
                        setCiEditing(false)
                        setCiError(null)
                      }}
                      disabled={ciSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      {t('userManagement.cardIssuedCancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : user.cardIssuedAt ? (
                <dl className="space-y-3 text-sm">
                  <DetailRow
                    label={t('userManagement.cardIssuedStatus', 'Status')}
                    value={
                      <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700 ring-1 ring-inset ring-green-200">
                        {t('userManagement.cardIssuedYes', 'Yes')}
                      </span>
                    }
                  />
                  <DetailRow
                    label={t('userManagement.cardIssuedAt', 'Date & Time Issued')}
                    value={new Date(user.cardIssuedAt).toLocaleString()}
                  />
                  <DetailRow label={t('userManagement.cardIssuedBy', 'Issued By')} value={user.cardIssuedBy || '—'} />
                </dl>
              ) : (
                <p className="text-sm text-gray-500">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 ring-1 ring-inset ring-gray-200">
                      {t('userManagement.cardIssuedNo', 'No')}
                    </span>
                    <span className="italic">
                      {t('userManagement.cardIssuedNotYet', 'Physical card not recorded as issued yet')}
                    </span>
                  </span>
                </p>
              )}
            </div>

            {/* Reset password (admin sets a new password for the member) */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('userManagement.resetPassword', 'Reset Password')}
                </h2>
                {!pwEditing && (
                  <button
                    onClick={() => {
                      setPwNew('')
                      setPwConfirm('')
                      setPwShow(false)
                      setPwError(null)
                      setPwEditing(true)
                    }}
                    disabled={pwSaving}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-40"
                  >
                    {t('userManagement.resetPasswordUpdate', 'Reset Password')}
                  </button>
                )}
              </div>

              {pwEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('userManagement.newPassword', 'New Password')}
                    </label>
                    <div className="relative">
                      <input
                        type={pwShow ? 'text' : 'password'}
                        value={pwNew}
                        onChange={(e) => setPwNew(e.target.value)}
                        className="w-full rounded-md border border-neutral-300 px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                      <button
                        type="button"
                        onClick={() => setPwShow(!pwShow)}
                        className="absolute inset-y-0 right-0 px-3 text-xs text-neutral-500 hover:text-neutral-700"
                      >
                        {pwShow ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      {t('userManagement.confirmPassword', 'Confirm Password')}
                    </label>
                    <input
                      type={pwShow ? 'text' : 'password'}
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      checked={pwReset2fa}
                      onChange={(e) => setPwReset2fa(e.target.checked)}
                      className="rounded border-neutral-300"
                    />
                    {t('userManagement.reset2fa', 'Also reset two-factor authentication (needed if the member lost their authenticator)')}
                  </label>
                  {pwError && <p className="text-red-600 text-xs">{pwError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSavePassword}
                      disabled={pwSaving}
                      className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
                    >
                      {pwSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setPwEditing(false)
                        setPwError(null)
                      }}
                      disabled={pwSaving}
                      className="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  {user.twoFactorEnabled && (
                    <p className="text-amber-700">
                      Two-factor authentication is enabled — tick "reset two-factor" when resetting if the
                      member lost their authenticator.
                    </p>
                  )}
                  {user.passwordResetAt ? (
                    <p className="text-neutral-700">
                      Password last reset {new Date(user.passwordResetAt).toLocaleString()}.
                    </p>
                  ) : (
                    <p className="text-neutral-500 italic">No password reset recorded.</p>
                  )}
                  <p className="text-xs text-neutral-500">
                    The member's other sessions stay active until their login token expires (up to 7 days).
                  </p>
                </div>
              )}
            </div>

            {user.serviceProvider && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('userManagement.serviceProvider', 'Service Provider')}
                </h2>
                <dl className="space-y-3 text-sm">
                  <DetailRow label={t('userManagement.companyName', 'Company')} value={user.serviceProvider.companyName} />
                  <DetailRow label={t('userManagement.serviceType', 'Service Type')} value={user.serviceProvider.serviceType} />
                  <DetailRow label={t('userManagement.rating', 'Rating')} value={`${user.serviceProvider.rating} (${user.serviceProvider.reviewCount})`} />
                  <DetailRow
                    label={t('userManagement.providerStatus', 'Provider Status')}
                    value={
                      user.serviceProvider.isActive
                        ? t('userManagement.active', 'Active')
                        : t('userManagement.inactive', 'Inactive')
                    }
                  />
                </dl>
              </div>
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="bg-white rounded-xl shadow-md p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('userManagement.timestamps', 'Timestamps')}
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <DetailRow
              label={t('userManagement.registered', 'Registered')}
              value={new Date(user.createdAt).toLocaleString()}
            />
            <DetailRow
              label={t('userManagement.lastUpdated', 'Last Updated')}
              value={new Date(user.updatedAt).toLocaleString()}
            />
          </dl>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({
  ok,
  okLabel,
  notLabel,
}: {
  label: string
  ok: boolean
  okLabel: string
  notLabel: string
}) {
  return (
    <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3">
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${
          ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {ok ? okLabel : notLabel}
      </span>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium text-right">{value}</dd>
    </div>
  )
}

export default UserDetailPage
